/**
 * Image optimization utilities for performance
 */

/**
 * Optimizes image URLs with modern formats and responsive sizing
 */
export const optimizeImageUrl = (url: string, options: {
  width?: number;
  height?: number;
  format?: 'webp' | 'avif' | 'auto';
  quality?: number;
} = {}) => {
  const { width, height, format = 'auto', quality = 75 } = options;
  
  // For Unsplash images, add optimization parameters
  if (url.includes('images.unsplash.com')) {
    const params = new URLSearchParams();
    if (width) params.set('w', width.toString());
    if (height) params.set('h', height.toString());
    params.set('fit', 'crop');
    params.set('auto', 'format');
    params.set('q', quality.toString());
    
    return `${url}?${params.toString()}`;
  }
  
  // For Supabase/Lovable uploads, we can't modify them directly
  // but we can add CSS optimizations
  return url;
};

/**
 * Generates responsive image sizes attribute
 */
export const generateImageSizes = (breakpoints: {
  mobile?: string;
  tablet?: string;
  desktop?: string;
} = {}) => {
  const {
    mobile = '100vw',
    tablet = '50vw', 
    desktop = '33vw'
  } = breakpoints;
  
  return `(max-width: 768px) ${mobile}, (max-width: 1024px) ${tablet}, ${desktop}`;
};

/**
 * Preloads critical images
 */
export const preloadCriticalImage = (url: string, as: 'image' = 'image') => {
  if (typeof window !== 'undefined') {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = as;
    link.href = url;
    document.head.appendChild(link);
  }
};

/**
 * Lazy loads images with intersection observer
 */
export const setupLazyLoading = () => {
  if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
};